import React, { ReactNode, useMemo } from "react";

import { ChipCloseIcon, ChipContainer, ChipTrigger } from "./Styles";
import { InputSize } from "../Input/types";
import { Typography, ITypographyProps } from "../Typography/Typography";

export interface ChipProps {
  label: string;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  startAdornment?: ReactNode;
  size?: InputSize;
  color?: string;
  backgroundColor?: string;
  closeClassName?: string;
  typographyProps?: ITypographyProps;
}

export const Chip = React.forwardRef<
  HTMLDivElement,
  ChipProps & Omit<React.ComponentPropsWithoutRef<"div">, keyof ChipProps>
>(
  (
    {
      startAdornment,
      label,
      onClose,
      size = "regular",
      color,
      backgroundColor,
      closeClassName,
      typographyProps,
      ...rest
    },
    ref
  ) => {
    const showClose = typeof onClose === "function";
    const variant = useMemo(() => {
      switch (size) {
        case "big":
          return "Body 1";
        case "biggest":
          return "Body 1";
        default:
          return "Caption 1";
      }
    }, [size]);

    return (
      <ChipContainer
        {...rest}
        ref={ref}
        size={size}
        showClose={showClose}
        color={color}
        backgroundColor={backgroundColor}
        data-testid="chip-container"
      >
        {startAdornment}
        <Typography
          variant={variant}
          data-testid="chip-typography"
          weight={size === "biggest" ? "medium" : "regular"}
          {...typographyProps}
        >
          {label}
        </Typography>
        {showClose ? (
          <ChipTrigger size={size} onClick={onClose} className={closeClassName}>
            <ChipCloseIcon fill={color ? color : "currentColor"} />
          </ChipTrigger>
        ) : null}
      </ChipContainer>
    );
  }
);

Chip.displayName = "Chip";
